import { useCallback, useMemo, useState } from "react";
import type { PlaybackTelemetryPayload } from "@/components/MuxVideoPlayerWithCTA";

/** Segundos de reproducción para considerar al visitor listo para convertir. */
const ENGAGED_WATCH_SECONDS = 10;

export type MembershipCtaEngagement = {
  videoPlaying: boolean;
  videoEnded: boolean;
  videoReady: boolean;
  watchedSeconds: number;
  /** Tras ver suficiente video o terminar un ciclo — enfatizar CTA VIP. */
  engaged: boolean;
  setVideoReady: (ready: boolean) => void;
  onVideoPlay: () => void;
  onVideoEnded: () => void;
  onPlaybackTelemetry: (payload: PlaybackTelemetryPayload) => void;
};

export function useMembershipCtaEngagement(): MembershipCtaEngagement {
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [watchedSeconds, setWatchedSeconds] = useState(0);
  const [durationSeconds, setDurationSeconds] = useState<number | null>(null);

  const onVideoPlay = useCallback(() => {
    setVideoReady(true);
    setVideoPlaying(true);
  }, []);

  const onVideoEnded = useCallback(() => {
    setVideoEnded(true);
    setVideoPlaying(false);
  }, []);

  const onPlaybackTelemetry = useCallback((payload: PlaybackTelemetryPayload) => {
    setWatchedSeconds((prev) => Math.max(prev, payload.currentTimeSeconds));
    if (payload.durationSeconds != null && payload.durationSeconds > 0) {
      setDurationSeconds(payload.durationSeconds);
    }
    if (payload.reason === "ended") {
      setVideoEnded(true);
    }
  }, []);

  const engaged = useMemo(() => {
    if (videoEnded) return true;
    if (watchedSeconds >= ENGAGED_WATCH_SECONDS) return true;
    if (durationSeconds != null && durationSeconds >= 20) {
      return watchedSeconds >= durationSeconds * 0.45;
    }
    return false;
  }, [videoEnded, watchedSeconds, durationSeconds]);

  return {
    videoPlaying,
    videoEnded,
    videoReady,
    watchedSeconds,
    engaged,
    setVideoReady,
    onVideoPlay,
    onVideoEnded,
    onPlaybackTelemetry,
  };
}